/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Grid,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Button,
  Typography,
  Modal,
  TextField,
  FormHelperText,
  Theme,
  SxProps,
  FormControlLabel,
} from "@mui/material";
import { RootState, useAppDispatch } from "@store/store";
import { formatTime, formatPhoneNumber } from "@helpers/funciones";
import "@styles/stylesRegistro.css";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { InactivityHandler } from "@components/InactivityHandler";
import { ApiError } from "@interfaces/response";
import CustomRightImage from "@components/CustomRightImagev2";
import * as Sentry from "@sentry/react";
import { CustomSwitch } from "@components/CustomSwitch";
import { CustomTextCiecBuro } from "@components/CustomTextCiecBuro";
import {
  useRequestPhoneCodeSecondGuaranteeMutation,
  useValidatePhoneCodeSecondGuaranteeMutation,
} from "@api/second_guarantee_api/secondGuaranteeApi";
import { setSecondGuaranteeBuroNip } from "@store/slices/segundoAvalSlice";
import ModalBuroNipSecondGuarantee from "@components/ModalBuroNipSecondGuarantee";

const TIME_LIMIT = 120;
const ONLY_NUMBERS_REGEX = /^[0-9]+$/;
const SCREEN_STATUS = "CAEMA";

const CustomTextField: SxProps<Theme> = (theme) => ({
  "& input": {
    textAlign: "center",
    fontSize: "25px",
    fontWeight: 400,
    width: "40px",
    height: "60px",
  },
  margin: "8px",
  backgroundColor: theme?.palette?.components?.phoneInput?.main || theme?.palette?.primary?.base,
  borderRadius: "10px",
  "& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
  },
  "& input[disabled]": {
    cursor: "not-allowed",
  },
});

const ConsultaBuroSegundoAval = () => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();

  const inputRef_1 = useRef<HTMLInputElement>(null);
  const inputRef_2 = useRef<HTMLInputElement>(null);
  const inputRef_3 = useRef<HTMLInputElement>(null);
  const inputRef_4 = useRef<HTMLInputElement>(null);

  const shareholder = useSelector((state: RootState) => state.secondGuarante.guarantee);

  const [validatePhoneCode] = useValidatePhoneCodeSecondGuaranteeMutation();
  const [requestPhoneCode] = useRequestPhoneCodeSecondGuaranteeMutation();

  const [secondsRemaining, setSecondsRemaining] = useState(TIME_LIMIT);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [openModalConfirm, setOpenModalConfirm] = useState(false);

  const formik = useFormik({
    initialValues: {
      code_1: "",
      code_2: "",
      code_3: "",
      code_4: "",
      switch: shareholder.buroNip?.termsAccepted || false,
    },
    validationSchema: yup.object().shape({
      code_1: yup.string(),
      code_2: yup.string(),
      code_3: yup.string(),
      code_4: yup.string(),
      switch: yup.boolean().oneOf([true], "Debes aceptar los términos y condiciones"),
    }),
    onSubmit: async () => {
      setOpenModalConfirm(true);
    },
  });

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const pasteData = e.clipboardData.getData("text").trim().slice(0, 4);

    if (pasteData.length === 4) {
      formik.setFieldValue("code_1", pasteData[0]);
      formik.setFieldValue("code_2", pasteData[1]);
      formik.setFieldValue("code_3", pasteData[2]);
      formik.setFieldValue("code_4", pasteData[3]);

      inputRef_4.current?.focus();
    }
  };

  const handleResendCode = async () => {
    try {
      setIsLoading(true);
      await requestPhoneCode({
        country_code: shareholder.personalInfo?.code_phone_number,
        phone_number: shareholder.personalInfo?.phone_number,
      }).unwrap();

      setSecondsRemaining(TIME_LIMIT);
    } catch (error) {
      const api_error = error as ApiError;
      if (api_error?.status === 429) {
        handleOpen();
        return;
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateCode = async () => {
    try {
      if (shareholder.buroNip?.isValid) {
        setErrorMessage(null);
        return;
      }

      const fullCode = [
        formik.values.code_1 || "",
        formik.values.code_2 || "",
        formik.values.code_3 || "",
        formik.values.code_4 || "",
      ].join("");

      if (fullCode.length < 4) {
        return;
      }

      setIsLoading(true);
      setErrorMessage(null);

      await validatePhoneCode({
        country_code: shareholder.personalInfo?.code_phone_number,
        phone_number: shareholder.personalInfo?.phone_number,
        code: fullCode,
      }).unwrap();

      dispatch(
        setSecondGuaranteeBuroNip({
          code: fullCode,
          isValid: true,
          termsAccepted: false,
        }),
      );
    } catch (error) {
      const api_error = error as ApiError;
      if (api_error?.status === 404 || api_error?.status === 400) {
        setErrorMessage("Código incorrecto, intenta de nuevo");
        formik.setFieldValue("code_1", "");
        formik.setFieldValue("code_2", "");
        formik.setFieldValue("code_3", "");
        formik.setFieldValue("code_4", "");
        return;
      }

      dispatch(setModalErrorVisible({ open: true, error: api_error }));
      Sentry.captureException(api_error, {
        extra: {
          component: "ConsultaBuroSegundoAval",
          values: formik.values,
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (secondsRemaining > 0) {
        setSecondsRemaining(secondsRemaining - 1);
      } else {
        clearInterval(intervalId);
      }
    }, 1000);
    return () => clearInterval(intervalId);
  }, [secondsRemaining]);

  useEffect(() => {
    validateCode();
  }, [formik.values.code_1, formik.values.code_2, formik.values.code_3, formik.values.code_4]);

  useEffect(() => {
    dispatch(
      setSecondGuaranteeBuroNip({
        code: shareholder.buroNip?.code,
        isValid: shareholder.buroNip?.isValid,
        termsAccepted: formik.values.switch,
      }),
    );
  }, [formik.values.switch]);

  useEffect(() => {
    if (shareholder.buroNip?.isValid) {
      formik.setFieldValue("code_1", shareholder.buroNip.code[0]);
      formik.setFieldValue("code_2", shareholder.buroNip.code[1]);
      formik.setFieldValue("code_3", shareholder.buroNip.code[2]);
      formik.setFieldValue("code_4", shareholder.buroNip.code[3]);
    }
  }, [shareholder.buroNip?.isValid]);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={4}>
        <InactivityHandler />
        <Box
          sx={{
            mt: isMobileOrTablet ? 2 : 3,
            mx: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            variant={isMobileOrTablet ? "h4" : "h3"}
            fontWeight={500}
            textAlign="center"
            color={theme?.palette?.primary?.dark}
          >
            Validación para consulta buró
          </Typography>

          <Box maxWidth={"sm"} mt={isMobileOrTablet ? 2 : 3} textAlign={"center"}>
            <Typography variant={isMobileOrTablet ? "body2" : "body1"} color={theme?.palette?.primary?.dark}>
              Ingresa el código que enviamos vía SMS y WhatsApp al número
            </Typography>
          </Box>

          <Typography
            variant={isMobileOrTablet ? "h5" : "h4"}
            mt={isMobileOrTablet ? 2 : 3}
            fontWeight={600}
            color={theme?.palette?.primary?.main}
          >
            ({shareholder.personalInfo?.code_phone_number}) {formatPhoneNumber(shareholder.personalInfo?.phone_number)}
          </Typography>

          {!shareholder.buroNip?.isValid && (
            <Typography
              mt={1}
              variant={isMobileOrTablet ? "body2" : "body2"}
              component={"a"}
              href="/avales/registro_aval"
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
              }}
              color={theme?.palette?.primary?.main}
            >
              ¿No es tu número? Edítalo aquí
            </Typography>
          )}

          <Typography
            mt={isMobileOrTablet ? 2 : 4}
            variant={isMobileOrTablet ? "body2" : "body1"}
            fontWeight={300}
            color={theme?.palette?.primary?.dark}
          >
            Código {shareholder.buroNip?.isValid ? "verificado" : "no verificado"}
          </Typography>

          <Container maxWidth="sm" sx={{ mt: isMobileOrTablet ? 2 : 1 }}>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Box display="flex" flexDirection="row" justifyContent="center">
                <TextField
                  type="text"
                  name="code_1"
                  variant="outlined"
                  margin="dense"
                  size="small"
                  autoComplete="off"
                  onChange={(e) => {
                    if (ONLY_NUMBERS_REGEX.test(e.target.value)) {
                      formik.handleChange(e);
                      inputRef_2.current?.focus();
                    }
                  }}
                  onBlur={formik.handleBlur}
                  onKeyUp={(e) => {
                    if (e.code === "Backspace") {
                      formik.setFieldValue("code_1", "");
                    }
                  }}
                  onPaste={handlePaste}
                  error={formik.touched.code_1 && Boolean(formik.errors.code_1)}
                  sx={CustomTextField(theme)}
                  inputProps={{
                    maxLength: 1,
                    inputMode: "numeric",
                  }}
                  inputRef={inputRef_1}
                  disabled={isLoading || formik.isSubmitting || shareholder.buroNip?.isValid}
                  value={formik.values.code_1}
                />

                <TextField
                  type="text"
                  name="code_2"
                  variant="outlined"
                  margin="dense"
                  size="small"
                  autoComplete="off"
                  onChange={(e) => {
                    if (ONLY_NUMBERS_REGEX.test(e.target.value)) {
                      formik.handleChange(e);
                      inputRef_3.current?.focus();
                    }
                  }}
                  onBlur={formik.handleBlur}
                  onKeyUp={(e) => {
                    if (e.code === "Backspace" && !formik.values.code_2) {
                      inputRef_1.current?.focus();
                    } else if (e.code === "Backspace") {
                      formik.setFieldValue("code_2", "");
                    }
                  }}
                  onPaste={handlePaste}
                  error={formik.touched.code_2 && Boolean(formik.errors.code_2)}
                  sx={CustomTextField(theme)}
                  inputProps={{
                    maxLength: 1,
                    inputMode: "numeric",
                  }}
                  inputRef={inputRef_2}
                  disabled={isLoading || formik.isSubmitting || shareholder.buroNip?.isValid}
                  value={formik.values.code_2}
                />

                <TextField
                  type="text"
                  name="code_3"
                  variant="outlined"
                  margin="dense"
                  size="small"
                  autoComplete="off"
                  onChange={(e) => {
                    if (ONLY_NUMBERS_REGEX.test(e.target.value)) {
                      formik.handleChange(e);
                      inputRef_4.current?.focus();
                    }
                  }}
                  onBlur={formik.handleBlur}
                  onKeyUp={(e) => {
                    if (e.code === "Backspace" && !formik.values.code_3) {
                      inputRef_2.current?.focus();
                    } else if (e.code === "Backspace") {
                      formik.setFieldValue("code_3", "");
                    }
                  }}
                  onPaste={handlePaste}
                  error={formik.touched.code_3 && Boolean(formik.errors.code_3)}
                  sx={CustomTextField(theme)}
                  inputProps={{
                    maxLength: 1,
                    inputMode: "numeric",
                  }}
                  inputRef={inputRef_3}
                  disabled={isLoading || formik.isSubmitting || shareholder.buroNip?.isValid}
                  value={formik.values.code_3}
                />

                <TextField
                  type="text"
                  name="code_4"
                  variant="outlined"
                  margin="dense"
                  size="small"
                  autoComplete="off"
                  onChange={(e) => {
                    if (ONLY_NUMBERS_REGEX.test(e.target.value)) {
                      formik.handleChange(e);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  onKeyUp={(e) => {
                    if (e.code === "Backspace" && !formik.values.code_4) {
                      inputRef_3.current?.focus();
                    } else if (e.code === "Backspace") {
                      formik.setFieldValue("code_4", "");
                    }
                  }}
                  onPaste={handlePaste}
                  error={formik.touched.code_4 && Boolean(formik.errors.code_4)}
                  sx={CustomTextField(theme)}
                  inputProps={{
                    maxLength: 1,
                    inputMode: "numeric",
                  }}
                  inputRef={inputRef_4}
                  disabled={isLoading || formik.isSubmitting || shareholder.buroNip?.isValid}
                  value={formik.values.code_4}
                />
              </Box>

              {errorMessage && <FormHelperText sx={{ mt: isMobileOrTablet ? 2 : 1 }}>{errorMessage}</FormHelperText>}

              {!shareholder.buroNip?.isValid && (
                <>
                  <Typography
                    variant={isMobileOrTablet ? "body2" : "body1"}
                    mt={isMobileOrTablet ? 2 : 3}
                    textAlign="center"
                    fontWeight={600}
                    color={theme?.palette?.primary?.main}
                  >
                    {formatTime(secondsRemaining)} min
                  </Typography>

                  {secondsRemaining === 0 && (
                    <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3}>
                      <Typography
                        variant={isMobileOrTablet ? "body2" : "body1"}
                        component={"a"}
                        onClick={handleResendCode}
                        sx={{ cursor: "pointer", textDecoration: "underline" }}
                        color={theme?.palette?.primary?.dark}
                      >
                        Reenviar código
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              <Box display={"flex"} justifyContent={"center"} mt={isMobileOrTablet ? 2 : 3} textAlign="center">
                <FormControlLabel
                  control={<CustomSwitch />}
                  label={
                    <Box sx={{ paddingTop: "10px" }}>
                      <Typography variant="body2" color={theme?.palette?.primary?.dark}>
                        <CustomTextCiecBuro type="nipBuro" />
                      </Typography>
                    </Box>
                  }
                  name="switch"
                  checked={formik.values.switch}
                  onChange={formik.handleChange}
                  disabled={isLoading || formik.isSubmitting || !shareholder.buroNip?.isValid}
                />
              </Box>

              <Box display="flex" justifyContent="center" mt={isMobileOrTablet ? 2 : 3}>
                <Button
                  variant="default"
                  type="submit"
                  color="primary"
                  disabled={isLoading || !shareholder.buroNip?.isValid || formik.isSubmitting || !formik.isValid}
                >
                  Continuar
                </Button>
              </Box>
            </form>
          </Container>
        </Box>
      </Grid>

      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            textAlign: "center",
          }}
        >
          <Typography fontWeight={"600"} gutterBottom color={theme?.palette?.primary?.dark} fontSize={"20px"}>
            Excedió el número de intentos permitidos para ese número
          </Typography>
          <Typography fontSize={"16px"} sx={{ mt: 2, mb: 2 }} color={theme?.palette?.primary?.dark}>
            intenta más tarde o contacta a un asesor.
          </Typography>
          <Button variant="default" color="primary" onClick={handleClose}>
            Cerrar
          </Button>
        </Box>
      </Modal>

      <ModalBuroNipSecondGuarantee
        screenStatus={SCREEN_STATUS}
        open={openModalConfirm}
        onClose={() => setOpenModalConfirm(false)}
      />
    </Grid>
  );
};

export default ConsultaBuroSegundoAval;
